/**
 * ProductDetails Component
 * Full product page with gallery, options, and add to cart
 */

import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import type { Product, ProductVariant } from '~/lib/shopify/types';
import { formatPrice, calculateDiscount } from '~/lib/shopify/api';
import { useCartStore } from '~/store/cart-store';

interface ProductDetailsProps {
  product: Product;
}

export function ProductDetails({ product }: ProductDetailsProps) {
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>(() => {
    // Prefer the first in-stock variant; fall back to the first variant
    const inStockVariant = product.variants.edges.find((e) => e.node.availableForSale)?.node
      ?? product.variants.edges[0]?.node;
    const initial: Record<string, string> = {};
    product.options.forEach((option) => {
      const match = inStockVariant?.selectedOptions.find((o) => o.name === option.name);
      initial[option.name] = match?.value ?? option.values[0];
    });
    return initial;
  });
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const [activeTab, setActiveTab] = useState<'description' | 'specs' | 'shipping'>('description');

  const addItem = useCartStore((state) => state.addItem);
  const openCart = useCartStore((state) => state.openCart);

  // Find matching variant
  const selectedVariant = useMemo(() => {
    return product.variants.edges.find((edge) => {
      return edge.node.selectedOptions.every(
        (option) => selectedOptions[option.name] === option.value
      );
    })?.node;
  }, [product.variants, selectedOptions]);

  const images = product.images.edges.map((e) => e.node);
  const currentImage = images[selectedImageIndex] || product.featuredImage;
  const price = selectedVariant?.price || product.priceRange.minVariantPrice;
  const compareAtPrice = selectedVariant?.compareAtPrice;
  const hasDiscount = compareAtPrice && parseFloat(compareAtPrice.amount) > parseFloat(price.amount);
  const discountPercent = hasDiscount ? calculateDiscount(price.amount, compareAtPrice.amount) : 0;
  const isAvailable = selectedVariant?.availableForSale ?? product.availableForSale;

  const handleOptionChange = (optionName: string, value: string) => {
    setSelectedOptions((prev) => ({ ...prev, [optionName]: value }));
  };

  const handleAddToCart = async () => {
    if (!selectedVariant || !isAvailable) return;

    setIsAdding(true);
    try {
      await addItem(selectedVariant.id, quantity);
      openCart();
    } finally {
      setIsAdding(false);
    }
  };

  // Parse metafields for specs
  const specs = useMemo(() => {
    const specsField = product.metafields?.filter(Boolean).find((m) => m.key === 'specifications');
    if (specsField?.value) {
      try {
        return JSON.parse(specsField.value) as Array<{ label: string; value: string }>;
      } catch {
        return [];
      }
    }
    return [];
  }, [product.metafields]);

  return (
    <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
      {/* Image Gallery */}
      <div className="space-y-4">
        {/* Main Image */}
        <motion.div
          className="aspect-square bg-surface-coal rounded-2xl overflow-hidden relative p-4"
          layoutId={`product-image-${product.id}`}
        >
          {/* Badges */}
          <div className="absolute top-6 left-6 z-10 flex gap-2">
            {hasDiscount && <span className="badge-sale">-{discountPercent}% Off</span>}
            {product.tags.includes('new') && <span className="badge-new">New</span>}
            {product.tags.includes('refurbished') && <span className="bg-amber-600 text-white text-xs font-bold px-2.5 py-1 rounded-full">Refurbished</span>}
          </div>

          {/* White background container for product image */}
          <div className="w-full h-full bg-white rounded-xl overflow-hidden flex items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.img
                key={selectedImageIndex}
                src={currentImage?.url}
                alt={currentImage?.altText || product.title}
                width={500}
                height={500}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="max-w-full max-h-full w-auto h-auto object-contain p-6"
              />
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Thumbnails */}
        {images.length > 1 && (
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {images.map((image, index) => (
              <button
                key={image.url}
                onClick={() => setSelectedImageIndex(index)}
                className={`gallery-thumb flex-shrink-0 w-20 h-20 ${
                  index === selectedImageIndex ? 'active' : ''
                }`}
              >
                <div className="w-full h-full bg-white rounded-lg overflow-hidden flex items-center justify-center">
                  <img
                    src={image.url}
                    alt={image.altText || `${product.title} ${index + 1}`}
                    width={72}
                    height={72}
                    className="max-w-full max-h-full w-auto h-auto object-contain p-1"
                  />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="space-y-6">
        {/* Brand */}
        <span className="badge-brand">{product.vendor}</span>

        {/* Title */}
        <h1 className="heading-md text-accent-white">{product.title}</h1>

        {/* Price */}
        <div className="space-y-1">
          <div className="flex items-baseline gap-3">
            <span className={`text-3xl font-display font-bold ${hasDiscount ? 'text-brand-orange' : 'text-accent-white'}`}>
              {formatPrice(price.amount, price.currencyCode)}
            </span>
            {hasDiscount && compareAtPrice && (
              <span className="text-xl text-accent-silver line-through">
                {formatPrice(compareAtPrice.amount, compareAtPrice.currencyCode)}
              </span>
            )}
          </div>
          <p className="text-xs text-accent-silver">Price includes VAT</p>
        </div>

        {/* Stock Status */}
        <div className="flex items-center gap-2">
          {isAvailable ? (
            <>
              <span className="w-2 h-2 bg-emerald-500 rounded-full" />
              <span className="text-sm text-emerald-400">In Stock</span>
            </>
          ) : (
            <>
              <span className="w-2 h-2 bg-red-500 rounded-full" />
              <span className="text-sm text-red-400">Out of Stock</span>
            </>
          )}
        </div>

        <div className="divider" />

        {/* Options */}
        {product.options.filter((option) => !(option.name === 'Title' && option.values.length === 1 && option.values[0] === 'Default Title')).map((option) => (
          <div key={option.id}>
            <label className="block text-sm font-medium text-accent-white mb-3">
              {option.name}: <span className="text-accent-silver">{selectedOptions[option.name]}</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {option.values.map((value) => {
                const isSelected = selectedOptions[option.name] === value;
                // Check if ANY variant with this option value is available
                const isOptionAvailable = product.variants.edges.some(
                  (edge) =>
                    edge.node.availableForSale &&
                    edge.node.selectedOptions.some((o) => o.name === option.name && o.value === value)
                );

                return (
                  <button
                    key={value}
                    onClick={() => handleOptionChange(option.name, value)}
                    disabled={!isOptionAvailable}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      isSelected
                        ? 'bg-brand-orange text-white'
                        : isOptionAvailable
                        ? 'bg-surface-slate text-accent-platinum hover:bg-surface-ash'
                        : 'bg-surface-graphite text-accent-silver/50 cursor-not-allowed line-through'
                    }`}
                  >
                    {value}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {/* Quantity & Add to Cart */}
        <div className="flex gap-4">
          {/* Quantity Selector */}
          <div className="quantity-selector">
            <button
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              className="quantity-btn"
              aria-label="Decrease quantity"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
              </svg>
            </button>
            <span className="quantity-value">{quantity}</span>
            <button
              onClick={() => setQuantity(quantity + 1)}
              className="quantity-btn"
              aria-label="Increase quantity"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>

          {/* Add to Cart */}
          <button
            onClick={handleAddToCart}
            disabled={!isAvailable || isAdding}
            className="btn-primary flex-1"
          >
            {isAdding ? (
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Adding...
              </span>
            ) : isAvailable ? (
              'Add to Cart'
            ) : (
              'Out of Stock'
            )}
          </button>
        </div>

        {/* Trust Badges */}
        <div className="grid grid-cols-2 gap-3">
          <div className="trust-badge">
            <svg className="w-5 h-5 text-brand-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Free Next-Day Delivery</span>
          </div>
          <div className="trust-badge">
            <svg className="w-5 h-5 text-brand-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>30-Day Returns</span>
          </div>
          <div className="trust-badge">
            <svg className="w-5 h-5 text-brand-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span>2-Year Warranty</span>
          </div>
          <div className="trust-badge">
            <svg className="w-5 h-5 text-brand-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>UK Support</span>
          </div>
        </div>

        <div className="divider" />

        {/* Tabs */}
        <div>
          <div className="flex gap-1 border-b border-surface-slate">
            {[
              { id: 'description', label: 'Description' },
              ...(specs.length > 0 ? [{ id: 'specs', label: 'Specifications' }] : []),
              { id: 'shipping', label: 'Shipping' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === tab.id
                    ? 'border-brand-orange text-brand-orange'
                    : 'border-transparent text-accent-silver hover:text-accent-white'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="py-6">
            <AnimatePresence mode="wait">
              {activeTab === 'description' && (
                <motion.div
                  key="description"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="prose prose-invert prose-sm max-w-none prose-p:text-accent-silver prose-p:leading-relaxed prose-headings:text-accent-white prose-strong:text-accent-white prose-a:text-brand-orange prose-li:text-accent-silver prose-ul:space-y-1 prose-p:mb-4"
                  dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
                />
              )}

              {activeTab === 'specs' && (
                <motion.div
                  key="specs"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {specs.length > 0 ? (
                    <dl className="space-y-3">
                      {specs.map((spec, i) => (
                        <div
                          key={i}
                          className="flex justify-between py-2 border-b border-surface-slate last:border-0"
                        >
                          <dt className="text-accent-silver">{spec.label}</dt>
                          <dd className="text-accent-white font-medium">{spec.value}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : (
                    <p className="text-accent-silver">No specifications available.</p>
                  )}
                </motion.div>
              )}

              {activeTab === 'shipping' && (
                <motion.div
                  key="shipping"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-4"
                >
                  <div className="flex gap-3">
                    <div className="w-10 h-10 bg-surface-slate rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-brand-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium text-accent-white">Free Next-Day Delivery</h4>
                      <p className="text-sm text-accent-silver">On orders over £75. Order before 3pm for next-day delivery.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-10 h-10 bg-surface-slate rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-brand-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium text-accent-white">30-Day Returns</h4>
                      <p className="text-sm text-accent-silver">Not satisfied? Return within 30 days for a full refund.</p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
